import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  RefreshCw, MessageSquareText, Mail, Calendar, User,
  CheckCircle2, AlertTriangle, XCircle, Clock,
  Sparkles, ChevronRight, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  on_track:    { label: 'On Track',    icon: CheckCircle2,   iconWrap: 'bg-emerald-100', iconText: 'text-emerald-700', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', card: 'border-emerald-200 bg-emerald-50/50' },
  at_risk:     { label: 'At Risk',     icon: AlertTriangle,  iconWrap: 'bg-amber-100',   iconText: 'text-amber-700',   badge: 'bg-amber-50 text-amber-700 border-amber-200',     card: 'border-amber-200 bg-amber-50/50' },
  resolved:    { label: 'Resolved',    icon: CheckCircle2,   iconWrap: 'bg-blue-100',    iconText: 'text-blue-700',    badge: 'bg-blue-50 text-blue-700 border-blue-200',         card: 'border-blue-200 bg-blue-50/50' },
  escalated:   { label: 'Escalated',   icon: XCircle,        iconWrap: 'bg-red-100',     iconText: 'text-red-700',     badge: 'bg-red-50 text-red-700 border-red-200',           card: 'border-red-200 bg-red-50/50' },
  no_response: { label: 'No reply yet', icon: Clock,         iconWrap: 'bg-slate-100',   iconText: 'text-slate-700',   badge: 'bg-slate-50 text-slate-700 border-slate-200',     card: 'border-slate-200 bg-slate-50/50' },
};

const COMMITMENT_STATUS = {
  honoured: { label: 'Honoured', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  late:     { label: 'Late',     cls: 'bg-amber-50 text-amber-700 border-amber-200',       icon: AlertTriangle },
  pending:  { label: 'Pending',  cls: 'bg-slate-50 text-slate-700 border-slate-200',       icon: Clock },
  missed:   { label: 'Missed',   cls: 'bg-red-50 text-red-700 border-red-200',             icon: XCircle },
};

function formatGmailDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return d; }
}

function senderLabel(from) {
  if (!from) return '(unknown)';
  const m = from.match(/^([^<]+?)\s*<([^>]+)>/);
  if (m) return m[1].trim();
  return from;
}

function senderInitials(from) {
  const name = senderLabel(from);
  return name.split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase() || '?';
}

// ── Body cleaners ─────────────────────────────────────────────────────
// Detect the "On <date>, <person> wrote:" / "From: ..." quoted-reply marker so
// we can collapse everything below it into a "Show quoted text" toggle.
const QUOTED_RE = /\n(?:On\s.{5,160}?\s+wrote:|-{2,}\s*Original\s+Message\s*-{2,}|From:\s.+?\nSent:|Begin\s+forwarded\s+message:)/i;

function splitQuoted(text) {
  if (!text) return { main: '', quoted: '' };
  const m = text.search(QUOTED_RE);
  if (m === -1) return { main: text, quoted: '' };
  return { main: text.slice(0, m).trimEnd(), quoted: text.slice(m).trimStart() };
}

// HTML-quoted-section detector for Gmail's typical `<blockquote>` wrappers
// + "On … wrote:" lines that Gmail wraps in a div.
function splitQuotedHtml(html) {
  if (!html) return { main: '', quoted: '' };
  // Cut at the first <blockquote> (Gmail's reply quote marker)
  const i1 = html.search(/<blockquote[\s>]/i);
  // Cut at the "<div ... gmail_quote">"
  const i2 = html.search(/<div[^>]*gmail_quote[^>]*>/i);
  // Cut at "On <date>, <name> wrote:" plain-text marker that survived the HTML
  const i3 = html.search(/On\s.{5,160}?\s+wrote:/i);
  const candidates = [i1, i2, i3].filter(i => i !== -1);
  if (!candidates.length) return { main: html, quoted: '' };
  const cut = Math.min(...candidates);
  return { main: html.slice(0, cut).trim(), quoted: html.slice(cut) };
}

// Sanitise inbound HTML — strip <script>/<style>, on* event handlers, and
// dangerous URI schemes. Conservative; we keep most formatting.
function sanitiseHtml(html) {
  if (!html) return '';
  let h = String(html);
  h = h.replace(/<script\b[\s\S]*?<\/script>/gi, '');
  h = h.replace(/<style\b[\s\S]*?<\/style>/gi, '');
  h = h.replace(/\son\w+\s*=\s*"[^"]*"/gi, '');
  h = h.replace(/\son\w+\s*=\s*'[^']*'/gi, '');
  h = h.replace(/\son\w+\s*=\s*[^\s>]+/gi, '');
  h = h.replace(/href\s*=\s*"javascript:[^"]*"/gi, 'href="#"');
  h = h.replace(/href\s*=\s*'javascript:[^']*'/gi, "href='#'");
  return h;
}

// Plain-text → simple HTML: linkify URLs, escape angle brackets, preserve newlines
function plainTextToHtml(text) {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Linkify
  const linked = escaped.replace(
    /(https?:\/\/[^\s<>"]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary underline">$1</a>'
  );
  // Group consecutive non-blank lines into paragraphs
  return linked
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

// ── Single message body ──────────────────────────────────────────────
function MessageBody({ msg }) {
  const [showQuoted, setShowQuoted] = React.useState(false);

  const html = msg.body_html ? sanitiseHtml(msg.body_html) : '';
  const { main: mainHtml, quoted: quotedHtml } = splitQuotedHtml(html);
  const text = msg.body || '';
  const { main: mainText, quoted: quotedText } = splitQuoted(text);

  // Prefer HTML body when present; fall back to plain text
  const usingHtml = !!html;
  const main      = usingHtml ? mainHtml : plainTextToHtml(mainText || msg.snippet || '(no body)');
  const quoted    = usingHtml ? quotedHtml : (quotedText ? plainTextToHtml(quotedText) : '');

  return (
    <div className="text-sm text-foreground/85 leading-relaxed max-h-[420px] overflow-y-auto pr-2 thread-message-body">
      {main ? (
        <div dangerouslySetInnerHTML={{ __html: main }} />
      ) : (
        <p className="text-muted-foreground italic">(empty message)</p>
      )}

      {quoted && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowQuoted(s => !s)}
            data-testid="thread-toggle-quoted"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary border border-border rounded-md px-2 py-1 bg-white"
          >
            <span className="font-bold">···</span>
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
          {showQuoted && (
            <div
              className="mt-2 pl-3 border-l-2 border-border/60 text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: quoted }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Thread Timeline ───────────────────────────────────────────────────
export function ThreadTimeline({ thread }) {
  const messages = thread?.messages || [];
  const [expanded, setExpanded] = React.useState(() => {
    // Latest message expanded by default; others collapsed
    return new Set(messages.length > 0 ? [messages.length - 1] : []);
  });
  const [allExpanded, setAllExpanded] = React.useState(false);

  // Reset expansion when thread changes
  React.useEffect(() => {
    setExpanded(new Set(messages.length > 0 ? [messages.length - 1] : []));
    setAllExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded(new Set([messages.length - 1]));
      setAllExpanded(false);
    } else {
      setExpanded(new Set(messages.map((_, i) => i)));
      setAllExpanded(true);
    }
  };

  const toggleOne = (idx) => {
    const next = new Set(expanded);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setExpanded(next);
    setAllExpanded(next.size === messages.length);
  };

  if (!messages.length) {
    return (
      <div className="text-center py-12">
        <Mail className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-sm font-medium text-muted-foreground">No thread messages stored yet</p>
        <p className="text-xs text-muted-foreground mt-1">Click Refresh on the Progress tab to fetch the latest replies.</p>
      </div>
    );
  }

  return (
    <div data-testid="thread-timeline">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-[11px] text-muted-foreground">
          {messages.length} message{messages.length !== 1 ? 's' : ''} ·  click any to expand
        </p>
        {messages.length > 1 && (
          <button
            type="button"
            onClick={toggleAll}
            className="text-[11px] font-medium text-primary hover:underline"
            data-testid="thread-toggle-all"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      {/* Stacked email cards (Gmail-style) */}
      <div className="rounded-xl border border-border overflow-hidden bg-white shadow-sm">
        {messages.map((msg, idx) => (
          <ThreadEmailCard
            key={msg.gmail_message_id || idx}
            msg={msg}
            idx={idx}
            isFirst={idx === 0}
            isLast={idx === messages.length - 1}
            expanded={expanded.has(idx)}
            onToggle={() => toggleOne(idx)}
          />
        ))}
      </div>
    </div>
  );
}

function ThreadEmailCard({ msg, idx, isFirst, isLast, expanded, onToggle }) {
  return (
    <motion.div
      layout
      transition={{ duration: 0.18 }}
      className={`bg-white ${!isLast ? 'border-b border-border' : ''}`}
      data-testid={`thread-msg-${idx}`}
    >
      {/* Collapsed header (always visible, clickable) */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-secondary/30 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary text-[11px] font-bold">
          {senderInitials(msg.from)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground truncate">{senderLabel(msg.from)}</span>
            {expanded ? (
              <span className="text-[11px] text-muted-foreground truncate">
                to {addressLine(msg.to)}
                {msg.cc ? ` · cc ${addressLine(msg.cc)}` : ''}
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground truncate">
                {(msg.snippet || '').slice(0, 100)}{(msg.snippet || '').length > 100 ? '…' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
          {msg.attachments?.length > 0 && (
            <span className="text-[11px] text-blue-600 flex items-center gap-1">
              <Mail className="w-3 h-3" />{msg.attachments.length}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">{formatGmailDate(msg.date)}</span>
          {msg.gmail_message_id && (
            <a
              href={`https://mail.google.com/mail/u/0/#all/${msg.gmail_message_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-primary transition-colors"
              data-testid={`open-in-gmail-${idx}`}
              title="Open in Gmail"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.18 }}
          className="px-4 pb-4 pt-1"
        >
          {/* Recipient detail (To / Cc) */}
          <div className="text-[11px] text-muted-foreground mb-3 px-3 py-1.5 rounded bg-secondary/30 border border-border/40">
            <div><strong>To:</strong> {addressLine(msg.to)}</div>
            {msg.cc && <div className="mt-0.5"><strong>Cc:</strong> {addressLine(msg.cc)}</div>}
          </div>
          <MessageBody msg={msg} />
        </motion.div>
      )}
    </motion.div>
  );
}

function addressLine(value) {
  if (!value) return '';
  // "Name <email@x>, email2@y" → display labels
  const parts = String(value).split(/,(?![^<]*>)/).map(s => s.trim()).filter(Boolean);
  return parts.map(p => {
    const m = p.match(/^([^<]+?)\s*<(.+)>$/);
    if (m) return m[1].trim().replace(/^"(.*)"$/, '$1');
    return p;
  }).join(', ');
}

// ─── AI Progress Summary ───────────────────────────────────────────────
export function ProgressSummaryPanel({ audit, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);

  const summary = audit?.progress_summary;
  const statusKey = summary?.overall_status || 'no_response';
  const meta = STATUS_META[statusKey] || STATUS_META.no_response;
  const StatusIcon = meta.icon;

  const handleRefresh = async () => {
    if (!audit?._id) return;
    setRefreshing(true);
    try {
      const r = await fetch(`${BACKEND}/api/audits/${audit._id}/refresh-thread`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(`Thread refreshed — ${data.message_count} message(s)`);
      onRefreshed?.(data);
    } catch (e) {
      toast.error(`Refresh failed: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="progress-summary-panel">
      {/* Header / Status */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${meta.iconWrap} flex items-center justify-center flex-shrink-0`}>
            <StatusIcon className={`w-5 h-5 ${meta.iconText}`} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="text-sm font-semibold flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                AI Thread Tracker
              </h3>
              <Badge
                variant="outline"
                className={`text-[10px] uppercase tracking-wider ${meta.badge}`}
                data-testid="progress-status-badge"
              >
                {meta.label}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Detects commitments, deadlines &amp; whether the team kept their word.
            </p>
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={handleRefresh}
          disabled={refreshing || !audit?.gmail_thread_id}
          className="gap-1.5 h-8 text-xs"
          data-testid="refresh-thread-button"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh thread'}
        </Button>
      </div>

      {/* Headline */}
      {summary?.headline && (
        <Card className={meta.card}>
          <CardContent className="p-4">
            <p className="text-sm font-medium" data-testid="progress-headline">
              {summary.headline}
            </p>
            {summary.message_count > 0 && (
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Based on {summary.message_count} thread message{summary.message_count !== 1 ? 's' : ''}
                {summary.generated_at && ` · generated ${new Date(summary.generated_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* No summary yet */}
      {!summary && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <MessageSquareText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm font-medium text-muted-foreground">No thread analysis yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click Refresh to analyse the Gmail thread.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Commitments */}
      {summary?.commitments?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Commitments &amp; Deliveries
          </h4>
          <div className="space-y-2" data-testid="commitments-list">
            {summary.commitments.map((c, idx) => {
              const cs = COMMITMENT_STATUS[c.status] || COMMITMENT_STATUS.pending;
              const CIcon = cs.icon;
              return (
                <Card key={idx} className="border-border/70">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm font-semibold">{c.who || 'Team'}</span>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${cs.cls}`}>
                        <CIcon className="w-2.5 h-2.5" />
                        {cs.label}
                      </span>
                    </div>
                    <p className="text-xs text-foreground mb-1.5">{c.what}</p>
                    <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                      {(c.promised_week != null || c.promised_date) && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Promised: <span className="font-medium text-foreground/80">
                            {c.promised_week != null ? `W${c.promised_week}` : c.promised_date}
                          </span>
                        </span>
                      )}
                      {(c.actual_week != null || c.actual_date) && (
                        <span className="flex items-center gap-1">
                          <ChevronRight className="w-3 h-3" />
                          Actual: <span className="font-medium text-foreground/80">
                            {c.actual_week != null ? `W${c.actual_week}` : c.actual_date}
                          </span>
                        </span>
                      )}
                    </div>
                    {c.evidence && (
                      <div className="mt-2 px-2 py-1 rounded bg-secondary/50 text-[11px] italic text-muted-foreground border-l-2 border-border">
                        “{c.evidence}”
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Inconsistencies */}
      {summary?.inconsistencies?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            Inconsistencies
          </h4>
          <div className="space-y-1.5" data-testid="inconsistencies-list">
            {summary.inconsistencies.map((inc, idx) => {
              const sev = inc.severity || 'medium';
              const sevCls = sev === 'high'
                ? 'bg-red-50 border-red-200 text-red-800'
                : sev === 'low'
                ? 'bg-slate-50 border-slate-200 text-slate-700'
                : 'bg-amber-50 border-amber-200 text-amber-800';
              return (
                <div key={idx} className={`px-3 py-2 rounded-lg border text-xs ${sevCls}`}>
                  <span className="font-bold uppercase text-[10px] tracking-wider mr-2">{sev}</span>
                  {inc.description}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next step */}
      {summary?.next_step && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">Suggested next step</p>
            <p className="text-sm text-foreground" data-testid="progress-next-step">{summary.next_step}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
